import { Component, HostListener, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BaseCtl, ServiceLocatorService } from '@ncs/ncs-lib';
import { ChartOptions } from 'chart.js';
import { generateColors } from 'src/app/app.utils';

@Component({
  selector: 'app-ncs-graph',
  templateUrl: './ncs-graph.component.html',
  styleUrls: ['./ncs-graph.component.scss'],
})
export class NcsGraphComponent extends BaseCtl {
  @Input() attributes: any;
  @Input() data: any;
  @Input() collectionName: any;
  @Input() field: any;
  @Input() chartType: any;
  public chartdata: any;
  public loading: boolean = false;
  chartContainerWidth: string = '100%'; // Default width
  cardBodyHeight: string = 'auto';
  public filteredAttributes: any = [];
  public projection: any = null;
  private rawDataCache: number[] = []; // Default height

  chartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
      },
    },
    scales: {
      x: {
        display: true,
      },
      y: {
        display: true,
        beginAtZero: true,
      },
    },
  };

  constructor(
    public override locator: ServiceLocatorService,
    public override route: ActivatedRoute
  ) {
    super('', locator, route);
    let _self = this;
    locator.config.getProperty('endpoint.chart').then((property: string) => {
      this.initApiEndpoint(property);
    });
  }

  override preload(): void {}

  override ngOnInit() {
    let _self = this;
    _self.locator.config
      .getProperty('endpoint.chart')
      .then((property: string) => {
        _self.drawChart();
      });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event | null): void {
    const windowWidth = event
      ? (event.target as Window).innerWidth
      : window.innerWidth;

    if (windowWidth < 576) {
      this.chartContainerWidth = '90%'; // Small screens
    } else if (windowWidth < 768) {
      this.chartContainerWidth = '70%'; // Medium screens
    } else {
      this.chartContainerWidth = '70%'; // Larger screens
    }

    const windowHeight = event
      ? (event.target as Window).innerHeight
      : window.innerHeight;

    const headerHeight = 170; // Approximate height of header/form in px
    const footerHeight = 60; // Approximate height of footer (if any)
    const buffer = 50; // Optional extra buffer
    const availableHeight = windowHeight - headerHeight - footerHeight - buffer;
    this.cardBodyHeight = `${availableHeight}px`; // Set height dynamically
  }

  public activeProject(code: any) {
    let _self = this;

    _self.projection = code;
    if (_self.chartType === 'line') {
      _self.drawDateChart();
    } else {
    _self.drawNumberChart();
  }
  }

  public drawNumberChart() {
    let _self = this;
    let attr_code = this.collectionName + '.' + this.field['code'];
    let code = _self.field['code'];
    let searchData: any = {};
    const allowedTypes = ['text', 'textarea'];
    _self.filteredAttributes = this.attributes.filter((e: any) =>
      allowedTypes.includes(e['type'])
    );

    if (!_self.projection) {
      _self.projection = _self.filteredAttributes[0]['code'];
    }

    searchData['projection'] = _self.projection;
    let url = _self.api.endpoint + '/data/' + attr_code;
    _self.locator.http.post(
      url,
      searchData,
      function (res: any, error: boolean) {
        _self.loading = false;
        if (error) {
          alert('Server Error:' + error);
          return;
        }
        let data = res.result['data'];
        let labels = data.map((item: any) => item[_self.projection]);
        let chartData = data.map((item: any) => item[code]);
        const backgroundColors = generateColors(chartData.length);

        _self.chartOptions = {
          ..._self.chartOptions,
          scales: {
            x: {
              display: true,
            },
            y: {
              display: true,
              beginAtZero: true,
            },
          },
        };

        _self.chartdata = {
          labels: labels,
          datasets: [
            {
              label: _self.field['label'],
              data: chartData,
              backgroundColor: backgroundColors,
            },
          ],
        };
      }
    );
  }

  public drawDateChart() {
    let _self = this;
    let attr_code = this.collectionName + '.' + this.field['code'];
    let code = _self.field['code'];
    let searchData: any = {};
    const allowedTypes = ['number', 'decimal', 'expression'];
    _self.filteredAttributes = this.attributes.filter((e: any) =>
      allowedTypes.includes(e['type'])
    );

    if (!_self.projection) {
      _self.projection = _self.filteredAttributes[0]['code'];
    }

    searchData['projection'] = _self.projection;
    let url = _self.api.endpoint + '/data/' + attr_code;
    _self.locator.http.post(
      url,
      searchData,
      function (res: any, error: boolean) {
        _self.loading = false;
        if (error) {
          alert('Server Error:' + error);
          return;
        }
        let data = res.result['data'];

        let labels = data.map((item: any) => item[_self.field['code']]);
        let chartData = data.map((item: any) => item[_self.projection]);
        const backgroundColors = generateColors(chartData.length);

        _self.chartOptions = {
          ..._self.chartOptions,
          scales: {
            x: {
              display: true,
            },
            y: {
              display: true,
              beginAtZero: true,
            },
          },
        };

        _self.chartdata = {
          labels: labels,
          datasets: [
            {
              label: _self.field['label'],
              data: chartData,
              backgroundColor: backgroundColors,
            },
          ],
        };
      }
    );
  }

  public drawChart() {
    let _self = this;
    let searchData: any = {};

    _self.loading = true;
    let attr_code = this.collectionName + '.' + this.field['code'];
    const allowedTypes = ['text', 'textarea'];
    if (
      (this.field.type === 'number' || this.field.type === 'expression') &&
      this.attributes.filter((e: any) => allowedTypes.includes(e['type']))
        .length > 0
    ) {
      _self.drawNumberChart();
      return;
    }
    const allowedLineTypes = ['number', 'decimal', 'expression'];
    if (
      (this.field.type === 'date' || this.field.type === 'time') &&
      this.attributes.filter((e: any) => allowedLineTypes.includes(e['type']))
        .length > 0
    ) {
      _self.drawDateChart();
      return;
    }

    // if (
    //   this.field.type === 'text' ||
    //   this.field.type === 'list' ||
    //   this.field.type === 'number' ||
    //   this.field.type === 'expression' ||
    //   this.field.type === 'decimal' ||
    //   this.field.type === 'date'
    // ) {

      let url = _self.api.endpoint + '/data/' + attr_code;

      _self.locator.http.post(
        url,
        searchData,
        function (res: any, error: boolean) {
          _self.loading = false;
          if (error) {
            alert('Server Error:' + error);
            return;
          }
          let data = res.result['data'];
          let labels = Object.keys(data);
          let chartData = Object.values(data);
          const backgroundColors = generateColors(chartData.length);

          _self.chartdata = {
            labels: labels,
            datasets: [
              {
                label: _self.field['label'],
                data: chartData,
                backgroundColor: backgroundColors,
              },
            ],
          };

          if (_self.form.data.type == 'range') {
          _self.chartdata = _self.getNumericData(_self.field, _self.chartdata);
          }

          if (_self.form.data.showPercentage) {
            _self.chartdata.datasets[0].data = _self.convertToPercentages(
              _self.chartdata.datasets[0].data
            );
            _self.updateChartOptions(_self.form.data.showPercentage);
          }
        }
      );
    // } else {
    //   console.error('data not availeble');
    // }
  }

  getMaxValue(field: any, data: any): number {
    let labels = data.labels;
    return labels.length > 0 ? Math.max(...labels) : 0;
  }

  private getNumericData(field: any, data: any): any {
    let _self = this;
    let max_values = this.getMaxValue(field, data);
    const rangeSize = max_values / 4;
    const range1 = { min: 0, max: rangeSize }; // 0% - 25%
    const range2 = { min: rangeSize, max: 2 * rangeSize }; // 25% - 50%
    const range3 = { min: 2 * rangeSize, max: 3 * rangeSize }; // 50% - 75%
    const range4 = { min: 3 * rangeSize, max: max_values }; // 75% - 100%

    let countRange1 = 0,
      countRange2 = 0,
      countRange3 = 0,
      countRange4 = 0;
    let chartDatatemp = data.datasets[0].data;

    let labels_temp = data.labels;

    labels_temp.forEach((value: any, index: number) => {
      if (value !== undefined && value !== null) {
        if (value >= range1.min && value <= range1.max) {
          countRange1 = countRange1 + chartDatatemp[index];
        } else if (value > range2.min && value <= range2.max) {
          countRange2 = countRange2 + chartDatatemp[index];
        } else if (value > range3.min && value <= range3.max) {
          countRange3 = countRange3 + chartDatatemp[index];
        } else if (value > range4.min && value <= range4.max) {
          countRange4 = countRange4 + chartDatatemp[index];
        }
      }
    });

    let labels = [
      `${range1.min} - ${range1.max}`,
      `${range2.min} - ${range2.max}`,
      `${range3.min} - ${range3.max}`,
      `${range4.min} - ${range4.max}`,
    ];

    const totalCount = countRange1 + countRange2 + countRange3 + countRange4;

    let chartData = [countRange1, countRange2, countRange3, countRange4];
    data.labels = labels;
    data.datasets[0].data = chartData;
    const backgroundColors = generateColors(chartData.length);
    data.datasets[0].backgroundColors = backgroundColors;
    return data;
  }

  private convertToPercentages(data: number[]): number[] {
    const total = data.reduce((sum, value) => sum + value, 0);
    this.rawDataCache = [...data];
    return total > 0 ? data.map((value) => (value / total) * 100) : data;
  }

  private updateChartOptions(showPercentage: boolean) {
    let _self = this;
    this.chartOptions = {
      ...this.chartOptions,
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: function (value) {
              return showPercentage ? value + '%' : value;
            },
          },
        },
      },
      plugins: {
        ...this.chartOptions.plugins,
        tooltip: {
          callbacks: {
            label: function (tooltipItem) {
              const dataset = tooltipItem.dataset;
              const dataIndex = tooltipItem.dataIndex;
              const label = dataset.label || '';
              const value = dataset.data[dataIndex];
              const total = dataset.data.reduce((a: any, b: any) => a + b, 0);
              const percentage = ((value as number) / total) * 100;
              const rawValue = _self.rawDataCache[dataIndex];
              return showPercentage
                ? `${label}: ${rawValue} (${percentage.toFixed(2)}%)`
                : `${label}: ${rawValue}`;
            },
          },
        },
      },
    };
  }
}
